import api from './api';

export const reportService = {
  // AR Aging Report
  getARaging: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);
    if (params.format) searchParams.append('format', params.format);
    const qs = searchParams.toString();
    return api.get(`/reports/ar-aging${qs ? `?${qs}` : ''}`);
  },

  // Work in Progress Report
  getWIP: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.format) searchParams.append('format', params.format);
    const qs = searchParams.toString();
    return api.get(`/reports/wip${qs ? `?${qs}` : ''}`);
  },

  // Billable Hours Report
  getBillableHours: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);
    if (params.group_by) searchParams.append('group_by', params.group_by);
    if (params.format) searchParams.append('format', params.format);
    const qs = searchParams.toString();
    return api.get(`/reports/billable-hours${qs ? `?${qs}` : ''}`);
  },

  // Case Status Summary Report
  getCaseStatus: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);
    if (params.group_by) searchParams.append('group_by', params.group_by);
    if (params.format) searchParams.append('format', params.format);
    const qs = searchParams.toString();
    return api.get(`/reports/case-status${qs ? `?${qs}` : ''}`);
  },

  // Trust Balances Report
  getTrustBalances: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.format) searchParams.append('format', params.format);
    const qs = searchParams.toString();
    return api.get(`/reports/trust-balances${qs ? `?${qs}` : ''}`);
  },

  // Unbilled Activity Finder
  getUnbilledActivity: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.min_days_unbilled != null) searchParams.append('min_days_unbilled', params.min_days_unbilled);
    if (params.format) searchParams.append('format', params.format);
    const qs = searchParams.toString();
    return api.get(`/reports/unbilled-activity${qs ? `?${qs}` : ''}`);
  },

  // Effective Hourly Rate Report
  getEffectiveRate: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);
    if (params.format) searchParams.append('format', params.format);
    const qs = searchParams.toString();
    return api.get(`/reports/effective-rate${qs ? `?${qs}` : ''}`);
  },

  // Case List Report
  getCaseList: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);
    if (params.attorney_ids) searchParams.append('attorney_ids', params.attorney_ids);
    if (params.status_filter) searchParams.append('status_filter', params.status_filter);
    if (params.case_type_filter) searchParams.append('case_type_filter', params.case_type_filter);
    if (params.columns) searchParams.append('columns', params.columns);
    if (params.format) searchParams.append('format', params.format);
    const qs = searchParams.toString();
    return api.get(`/reports/case-list${qs ? `?${qs}` : ''}`, params.format === 'pdf' ? { responseType: 'blob' } : {});
  },

  // Revenue Report
  getRevenue: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);
    if (params.attorney_id) searchParams.append('attorney_id', params.attorney_id);
    if (params.practice_area) searchParams.append('practice_area', params.practice_area);
    if (params.client_id) searchParams.append('client_id', params.client_id);
    if (params.format) searchParams.append('format', params.format);
    const qs = searchParams.toString();
    return api.get(`/reports/revenue${qs ? `?${qs}` : ''}`);
  },

  // Time Log Detail Report
  getTimeLog: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);
    if (params.user_id) searchParams.append('user_id', params.user_id);
    if (params.is_billable != null) searchParams.append('is_billable', params.is_billable);
    if (params.is_billed != null) searchParams.append('is_billed', params.is_billed);
    if (params.case_id) searchParams.append('case_id', params.case_id);
    if (params.format) searchParams.append('format', params.format);
    const qs = searchParams.toString();
    return api.get(`/reports/time-log${qs ? `?${qs}` : ''}`);
  },

  // Revenue Report drill-down (single cell)
  getRevenueDrillDown: (params = {}) => {
    const searchParams = new URLSearchParams();
    searchParams.append('case_id', params.case_id);
    searchParams.append('type', params.type); // 'billed' | 'collected'
    searchParams.append('start_date', params.start_date);
    searchParams.append('end_date', params.end_date);
    if (params.attorney_id) searchParams.append('attorney_id', params.attorney_id);
    return api.get(`/reports/revenue/drill-down?${searchParams.toString()}`);
  },

  // Extract filename from Content-Disposition header, fall back to provided default
  _getFilename: (response, fallback) => {
    const disposition = response.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    return match ? match[1] : fallback;
  },

  // CSV download helper - triggers browser download
  downloadCSV: async (fetchFn, params, filename) => {
    try {
      const response = await fetchFn({ ...params, format: 'csv' });
      const resolvedName = reportService._getFilename(response, filename);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resolvedName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV download failed:', err);
      throw err;
    }
  },
  // PDF download helper - triggers browser download
  downloadPDF: async (fetchFn, params, filename) => {
    try {
      const response = await fetchFn({ ...params, format: 'pdf' });
      const resolvedName = reportService._getFilename(response, filename);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resolvedName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
      throw err;
    }
  },
};

export default reportService;
